import { useCallback, useEffect, useMemo } from 'react';
import { useRecoilValue } from 'recoil';

import { FAQ_LOG_EVENT } from '@/constants/logs/faq';
import { EPixelEventName } from '@/constants/pixel';
import useAppDownloadCujModal from '@/hooks/Cuj/useAppDownloadCujModal';
import useGetRelatedFaq from '@/hooks/faq/useGetRelatedFaq';
import useGetMagazineDetail from '@/hooks/magazine/useGetMagazineDetail';
import useGetPrevNextMagazinePid from '@/hooks/magazine/useGetPrevNextMagazinePid';
import useCanProgressQnaWrite from '@/hooks/qna/mobile/Write/useCanProgressQnaWrite';
import useGetMagazineRelatedQna from '@/hooks/qna/useGetMagazineRelatedQna';
import useAuthModal from '@/hooks/useAuthModal';
import useClickContentLike from '@/hooks/useClickContentLike';
import useCustomRouter from '@/hooks/useCustomRouter';
import useMoveToHospitalSearch from '@/hooks/useMoveToHospitalSearch';
import useMoveToTotalSearch from '@/hooks/useMoveToTotalSearch';
import useToast from '@/hooks/useToast';
import { tokenState } from '@/recoil/atoms/common/auth';
import { CustomPageProps } from '@/types/app';
import { ROUTES } from '@/types/common';
import {
  InternalMagazineTag,
  MagazineBottomButtonType,
  MagazineDetailVo,
  MagazineTagType,
  MagazineType,
  RelatedMagazinesVo,
} from '@/types/magazine';
import { RelatedQnaVo } from '@/types/qna';
import Airbridge from '@/utils/airbridge';
import { faqLogger, getMobileWebPageName, getPageName, logger, magazineLogger, mobileLogger } from '@/utils/logger';
import { NATIVE_POST_MESSAGE_TYPE, isWebView, sendPostMessageToNative } from '@/utils/mobile/RNPlugins';

import Pixel from '@/utils/pixel/pixel';

interface Props extends CustomPageProps {
  faqPid: string;
}

export interface FaqDetailViewModel {
  faq?: MagazineDetailVo;
  title: string;
  description: string;
  faqContent: string;
  magazineTocLogger: () => void;
  moveToHospital: (keyword: string) => void;
  departmentTags: InternalMagazineTag[];
  symptomTags: InternalMagazineTag[];
  diseaseTags: InternalMagazineTag[];
  moveToTotalSearch: (keyword: string) => void;
  faqTagNames: string[];
  onClickTag: (tagName: string) => void;
  copyUrl: string;
  onClickLike: () => void;
  handleLikeClick: () => void;
  onClickShare: () => void;
  onRecordLog: (logName: string) => void;
  handleStickyButton: () => Promise<undefined | void>;
  relatedFaq?: RelatedMagazinesVo[];
  moveOtherMagazine: (magazine: RelatedMagazinesVo) => void;
  faqRelatedQna?: RelatedQnaVo[];
  onClickRelatedQna: (questionPid: string) => void;
  prevFaqPid: string | null;
  nextFaqPid: string | null;
  onClickPrevNextFaq: (faqPid: string) => void;
  bottomButtonText: string;
  shouldShowEventText: () => boolean;
  onClickTopCta: () => void;
}

const useFaqDetailViewModel = ({ faqPid, client }: Props): FaqDetailViewModel => {
  const faq = useGetMagazineDetail(faqPid);
  const { moveToHospital } = useMoveToHospitalSearch();
  const { moveToTotalSearch } = useMoveToTotalSearch();
  const { moveToPage, router } = useCustomRouter();
  const token = useRecoilValue(tokenState);
  const { showToast } = useToast();
  const { clickProgressWrite } = useCanProgressQnaWrite();
  const { setShowAuthModal } = useAuthModal(true);
  const { likeContent, cancelLikeContent } = useClickContentLike(faqPid);
  const { relatedFaq } = useGetRelatedFaq(faqPid);
  const { prevMagazinePid: prevFaqPid, nextMagazinePid: nextFaqPid } = useGetPrevNextMagazinePid(
    faqPid,
    MagazineType.FAQ
  );
  const { magazineRelatedQna: faqRelatedQna } = useGetMagazineRelatedQna(faqPid);

  const pageName = !isWebView() ? ROUTES.FAQ.DETAIL.pageName : getMobileWebPageName(router);

  const copyUrl = useMemo(
    () => `${process.env.NEXT_PUBLIC_DOMAIN}${ROUTES.FAQ.DETAIL.path}/${faq?.magazinePid}`,
    [faq?.magazinePid]
  );

  const title = useMemo(() => {
    if (isWebView()) {
      return faq?.mobileTitle;
    }
    return faq?.title;
  }, [faq?.mobileTitle, faq?.title]);

  const description = useMemo(() => {
    if (isWebView()) {
      return faq?.mobileDescription;
    }
    return faq?.description;
  }, [faq?.mobileDescription, faq?.description]);

  const faqContent = useMemo(() => {
    if (isWebView()) {
      return faq?.mobileContent;
    }

    return faq?.content;
  }, [faq?.content, faq?.mobileContent]);

  const departmentTags = useMemo(
    () => faq?.tags.filter((tag) => tag.tagType === MagazineTagType.DEPARTMENT),
    [faq?.tags]
  );
  const diseaseTags = useMemo(() => faq?.tags.filter((tag) => tag.tagType === MagazineTagType.DISEASE), [faq?.tags]);
  const symptomTags = useMemo(() => faq?.tags.filter((tag) => tag.tagType === MagazineTagType.SYMPTOM), [faq?.tags]);

  const faqTagNames: string[] = useMemo(() => {
    return faq && faq.tagNames
      ? faq.tagNames.map((name) => {
          return name;
        })
      : [];
  }, [faq]);

  const bottomButtonText: string = useMemo(() => {
    switch (faq?.bottomButtonType) {
      case MagazineBottomButtonType.DIAGNOSIS:
        return '비대면 진료 받으러 가기';
      case MagazineBottomButtonType.QUESTION:
      default:
        return '지금 바로 의료 상담받기';
    }
  }, [faq?.bottomButtonType, client.isWebView]);

  const shouldShowEventText = () => !isWebView();

  const bottomButtonDynamicLink = () => {
    Airbridge.setDynamicLink({
      campaign: 'web_content_faq',
      content: 'web_bottom_flt_details',
      term: bottomButtonText,
      sub_id: faq?.magazinePid,
      sub_id_1: faq?.title,
      sub_id_2: diseaseTags?.[0]?.name || '',
      sub_id_3: departmentTags?.[0]?.name || '',
    });
  };

  const magazineTocLogger = () => {
    magazineLogger({
      eventName: FAQ_LOG_EVENT.CLICK_FAQ_TOC,
      pageName: getPageName(router),
      payload: {
        id: faqPid,
      },
    });
  };

  const onClickTag = (tagName: string) => {
    faqLogger({
      pageName: getPageName(router),
      eventName: FAQ_LOG_EVENT.CLICK_FAQ_TAG,
      payload: {
        faqPid: faqPid,
        tagType: MagazineTagType.FREE,
        tagName,
      },
    });
    moveToPage({
      webLink: `${ROUTES.FAQ.TAG.path}?tag=${tagName}`,
      mobileScreenName: ROUTES.FAQ.TAG.appScreenName,
      params: {
        tag: tagName,
      },
    });
  };

  const handleLikeClick = useCallback(() => {
    mobileLogger({
      logger: {
        eventName: `faq_content_detail_${faq?.isLikedByMe ? 'disLike' : 'like'}_button_click`,
        pageName,
      },
    });

    if (faq?.isLikedByMe) {
      cancelLikeContent();
      return;
    }
    likeContent();
  }, [cancelLikeContent, likeContent, faq?.isLikedByMe, pageName]);

  const onClickLike = () => {
    if (!token) {
      showToast({ message: '로그인 후 이용할 수 있어요.' });
      setShowAuthModal(true);
      return;
    }

    handleLikeClick();
  };

  const onClickShare = () => {
    logger({
      pageName: getPageName(router),
      eventName: FAQ_LOG_EVENT.UPPER_FAQ_SHARE_CLICK,
    });
  };

  const onRecordLog = (logName: string) => {
    logger({
      pageName: getPageName(router),
      eventName: logName,
    });
  };

  const handleStickyButton = async () => {
    faqLogger({
      eventName: FAQ_LOG_EVENT.DETAIL_STICKY_BUTTON_CLICK,
      pageName: getPageName(router),
      payload: {
        id: faqPid,
        type: faq?.bottomButtonType || '',
        platform: client.clientType,
      },
    });

    Pixel.trackCustom(EPixelEventName.BottomCtaClick, {
      ...(diseaseTags?.[0]?.name && { disease: diseaseTags?.[0]?.name }),
      ...(departmentTags?.[0]?.name && { subject: departmentTags?.[0]?.name }),
      ...(symptomTags?.[0]?.name && { symptom: symptomTags?.[0]?.name }),
    });

    switch (faq?.bottomButtonType) {
      case MagazineBottomButtonType.DIAGNOSIS:
        if (client.isWebView) {
          sendPostMessageToNative(NATIVE_POST_MESSAGE_TYPE.MOVE_TO_TAB_HOME, {
            tabName: 'HomeTab',
            screenName: ROUTES.HOME.appScreenName,
            params: { isNonFaceToFaceTreatmentRequestClick: true },
          });
        } else {
          bottomButtonDynamicLink();
        }

        break;
      case MagazineBottomButtonType.QUESTION:
      default:
        if (!client.isWebView) {
          bottomButtonDynamicLink();
          return;
        }

        await clickProgressWrite();
        break;
    }
  };

  const moveOtherMagazine = (magazine: RelatedMagazinesVo) => {
    moveToPage({
      webLink: magazine.isArticle
        ? `${ROUTES.MAGAZINE.DETAIL.path}/${magazine.magazinePid}`
        : `${ROUTES.FAQ.DETAIL.path}/${magazine.magazinePid}`,
      mobileScreenName: magazine.isArticle ? ROUTES.MAGAZINE.DETAIL.appScreenName : ROUTES.FAQ.DETAIL.appScreenName,
      params: {
        id: magazine.magazinePid,
      },
    });
  };

  const onClickRelatedQna = (questionPid: string) => {
    faqLogger({
      eventName: FAQ_LOG_EVENT.FAQ_RELATED_QNA_CLICK,
      pageName: getPageName(router),
      payload: {
        faqPid,
        questionPid,
      },
    });

    if (isWebView()) {
      sendPostMessageToNative(NATIVE_POST_MESSAGE_TYPE.MOVE_TO_TAB_HOME, {
        tabName: 'QuickAnswerTab',
        screenName: ROUTES.QNA.DETAIL.appScreenName,
        params: {
          questionId: questionPid,
        },
      });
    } else {
      router.push({
        pathname: `${ROUTES.QNA.DETAIL.path}/${questionPid}`,
      });
    }
  };

  const onClickPrevNextFaq = (faqPid: string) => {
    moveToPage({
      webLink: `${ROUTES.FAQ.DETAIL.path}/${faqPid}`,
      mobileScreenName: ROUTES.FAQ.DETAIL.appScreenName,
      params: {
        id: faqPid,
      },
    });
  };

  const onClickTopCta = () => {
    Pixel.trackCustom(EPixelEventName.TopCtaClick, {
      ...(diseaseTags?.[0]?.name && { disease: diseaseTags?.[0]?.name }),
      ...(departmentTags?.[0]?.name && { subject: departmentTags?.[0]?.name }),
      ...(symptomTags?.[0]?.name && { symptom: symptomTags?.[0]?.name }),
    });
  };

  const { setIsCujModalTrackingInfo } = useAppDownloadCujModal();

  useEffect(() => {
    if (!faq) {
      return;
    }

    Pixel.trackCustom(EPixelEventName.PageIn, {
      ...(diseaseTags?.[0]?.name && { disease: diseaseTags?.[0]?.name }),
      ...(departmentTags?.[0]?.name && { subject: departmentTags?.[0]?.name }),
      ...(symptomTags?.[0]?.name && { symptom: symptomTags?.[0]?.name }),
    });

    setIsCujModalTrackingInfo({
      campaignName: 'web_content_faq',
      trackContent: 'web_mid_mdl_details',
      contentId: faq.magazinePid,
      contentTitle: faq.title,
      contentCategory: diseaseTags?.[0]?.name || '',
      contentSubCategory: departmentTags?.[0]?.name || '',
    });
  }, [faq]);

  return {
    faq,
    title: title ?? '',
    description: description ?? '',
    faqContent: faqContent ?? '',
    magazineTocLogger,
    moveToHospital,
    departmentTags: departmentTags ?? [],
    symptomTags: symptomTags ?? [],
    diseaseTags: diseaseTags ?? [],
    moveToTotalSearch,
    faqTagNames,
    onClickTag,
    copyUrl,
    onClickLike,
    handleLikeClick,
    onClickShare,
    onRecordLog,
    handleStickyButton,
    relatedFaq,
    moveOtherMagazine,
    faqRelatedQna,
    onClickRelatedQna,
    prevFaqPid,
    nextFaqPid,
    onClickPrevNextFaq,
    bottomButtonText,
    shouldShowEventText,
    onClickTopCta,
  };
};

export default useFaqDetailViewModel;
